/**
 * @fileoverview Canvas Helper Utilities for Floor Plan
 * @author MCHD Room Scheduler Team
 * @created 09-10-2025 04:30:44
 * 
 * Utility functions for floor plan canvas operations:
 * - Room positioning and layout calculations
 * - Legend filtering and room opacity
 * - Canvas centering and bounds
 * - Grid navigation helpers
 */

// =============================================================================
// TYPES
// =============================================================================

export interface RoomPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Room {
  id: string;
  position: RoomPosition;
  isActive: boolean;
  blocks?: Array<{
    startDate: string;
    endDate: string;
  }>;
  assignments?: any[];
}

export interface CanvasBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  centerX: number;
  centerY: number;
}

// =============================================================================
// LAYOUT CALCULATIONS
// =============================================================================

/**
 * Calculate center point of all rooms for initial canvas centering
 * Per Floor Plan Spec Section 2: Canvas centers on first active room at 100% zoom
 * 
 * @param rooms - Array of rooms with positions
 * @returns Center coordinates or null if no rooms
 */
export function calculateRoomBoundsCenter(rooms: Room[]): { x: number; y: number } | null {
  if (!rooms || rooms.length === 0) return null;
  
  // Get first active room per spec
  const firstActiveRoom = rooms.find(r => r.isActive);
  
  if (firstActiveRoom) {
    // Center on first active room
    return {
      x: firstActiveRoom.position.x + firstActiveRoom.position.width / 2,
      y: firstActiveRoom.position.y + firstActiveRoom.position.height / 2
    };
  }
  
  // Fallback: Calculate geometric center of all rooms
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  
  rooms.forEach(room => {
    const { x, y, width, height } = room.position;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + width);
    maxY = Math.max(maxY, y + height);
  });
  
  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2
  };
}

/**
 * Calculate canvas bounds from room positions
 * 
 * @param rooms - Array of rooms with positions
 * @returns Bounding box coordinates
 */
export function calculateCanvasBounds(rooms: Room[]): CanvasBounds | null {
  if (!rooms || rooms.length === 0) return null;
  
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  
  rooms.forEach(room => {
    const { x, y, width, height } = room.position;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + width);
    maxY = Math.max(maxY, y + height);
  });
  
  return {
    minX,
    minY,
    maxX,
    maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2
  };
}

/**
 * Calculate stage position to center on specific point
 * 
 * @param centerPoint - Point to center on
 * @param canvasWidth - Canvas width
 * @param canvasHeight - Canvas height
 * @param scale - Current zoom scale
 * @returns Stage position { x, y }
 */
export function calculateCenteredStagePosition(
  centerPoint: { x: number; y: number },
  canvasWidth: number,
  canvasHeight: number,
  scale: number = 1
): { x: number; y: number } {
  return {
    x: canvasWidth / 2 - centerPoint.x * scale,
    y: canvasHeight / 2 - centerPoint.y * scale
  };
}

// =============================================================================
// LEGEND FILTERING
// =============================================================================

/**
 * Determine if room should be dimmed based on active legend filters
 * Per Floor Plan Spec Section 3: Dimmed to 30% opacity
 * 
 * @param room - Room to check
 * @param activeLegendFilters - Array of active filter IDs ('available', 'occupied', etc.)
 * @param selectedDate - Currently selected date
 * @returns Opacity value (1.0 normal, 0.3 dimmed)
 */
export function getRoomOpacityForLegendFilter(
  room: Room,
  activeLegendFilters: string[],
  selectedDate?: Date
): number {
  // No filters active - show all rooms normally
  if (activeLegendFilters.length === 0) return 1.0;
  
  // Determine room status ID
  const roomStatusId = getRoomStatusId(room, selectedDate);
  
  // Check if room matches any active filter
  const matches = activeLegendFilters.includes(roomStatusId);
  
  // Return opacity: 1.0 if matches, 0.3 if doesn't match (per spec)
  return matches ? 1.0 : 0.3;
}

/**
 * Get room status ID for legend filtering
 * Matches legend item IDs: 'available', 'occupied', 'blocked', 'inactive'
 * 
 * @param room - Room to check
 * @param selectedDate - Currently selected date
 * @returns Status ID string
 */
export function getRoomStatusId(room: Room, selectedDate?: Date): string {
  // Priority order per spec Section 4
  if (!room.isActive) return 'inactive';
  
  // Check if blocked for selected date
  if (room.blocks && room.blocks.length > 0 && selectedDate) {
    const hasBlockOnDate = room.blocks.some(block => {
      const startDate = new Date(block.startDate);
      const endDate = new Date(block.endDate);
      return selectedDate >= startDate && selectedDate <= endDate;
    });
    if (hasBlockOnDate) return 'blocked';
  }
  
  // Check if occupied (has assignments)
  if (room.assignments && room.assignments.length > 0) return 'occupied';
  
  // Default: available
  return 'available';
}

// =============================================================================
// GRID NAVIGATION
// =============================================================================

/**
 * Get room index for position in grid layout
 * Useful for keyboard navigation
 * 
 * @param rooms - Array of rooms
 * @param roomId - Room ID to find
 * @returns Index in array or -1 if not found
 */
export function getRoomIndex(rooms: Room[], roomId: string): number {
  return rooms.findIndex(r => r.id === roomId);
}

/**
 * Get next room in specified direction for arrow key navigation
 * 
 * @param rooms - Array of rooms
 * @param currentIndex - Current room index
 * @param direction - Navigation direction
 * @param roomsPerRow - Rooms per row in grid (default: 4)
 * @returns Next room or null if out of bounds
 */
export function getNextRoomInDirection(
  rooms: Room[],
  currentIndex: number,
  direction: 'up' | 'down' | 'left' | 'right',
  roomsPerRow: number = 4
): Room | null {
  let nextIndex = currentIndex;
  
  switch (direction) {
    case 'right':
      nextIndex = currentIndex + 1;
      break;
    case 'left':
      nextIndex = currentIndex - 1;
      break;
    case 'down':
      nextIndex = currentIndex + roomsPerRow;
      break;
    case 'up':
      nextIndex = currentIndex - roomsPerRow;
      break;
  }
  
  // Check bounds
  if (nextIndex < 0 || nextIndex >= rooms.length) {
    return null;
  }
  
  return rooms[nextIndex];
}

// =============================================================================
// PERFORMANCE HELPERS
// =============================================================================

/**
 * Check if floor has large number of rooms (80+)
 * Per Floor Plan Spec Section 10: Skip shadows for performance
 * 
 * @param roomCount - Number of rooms on floor
 * @returns True if large floor (80+ rooms)
 */
export function isLargeFloor(roomCount: number): boolean {
  return roomCount >= 80;
}

/**
 * Get shadow configuration based on room count
 * Large floors (80+): No shadow, use border instead
 * Normal floors: Use shadow for depth
 * 
 * @param roomCount - Total rooms on floor
 * @param isHovered - Whether room is currently hovered
 * @returns Shadow configuration object
 */
export function getShadowConfig(roomCount: number, isHovered: boolean): {
  shadowBlur: number;
  shadowOpacity: number;
} {
  const isLarge = isLargeFloor(roomCount);
  
  if (isLarge) {
    // Large floor: No shadow per spec
    return { shadowBlur: 0, shadowOpacity: 0 };
  }
  
  // Normal floor: Use shadow
  return {
    shadowBlur: isHovered ? 10 : 5,
    shadowOpacity: isHovered ? 0.3 : 0.2
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  calculateRoomBoundsCenter,
  calculateCanvasBounds,
  calculateCenteredStagePosition,
  getRoomOpacityForLegendFilter,
  getRoomStatusId,
  getRoomIndex,
  getNextRoomInDirection,
  isLargeFloor,
  getShadowConfig
};

